/* sb-dc-tilesync.js - edit one tile of a step & repeat board, the rest follow.
   2026-09-14 (v4).

   Reported (feedback item 2): an 8x8 backdrop opens with dozens of separate
   text boxes and logos. Putting in a company name means editing every tile by
   hand, so customers cannot realistically finish a design.

   The editor has no groups and no multi-select - selection is a single
   api.selectedId - so tiles cannot be linked in the app's own model. What this
   does instead: it notices that a set of layers are IDENTICAL COPIES of each
   other (same text, same styling), remembers them as a set, and when one of
   them is edited it applies the same edit to the others.

   WHY v4 - THE PICTURE COMING BACK AFTER A REPLACE
   updateLayer does not land in the same turn, and `src` on this board is a
   multi-megabyte data URL, so writing it to a dozen tiles takes longer than
   one tick. v3 looked again 400ms later, found the tiles that had not caught
   up yet, and - since the customer's own tile now matched the new baseline -
   treated ONE laggard as the next edit and pushed its OLD picture back over
   everything. The set is now left alone for COOL ticks after a propagation,
   and the baseline is re-read from the tile the customer actually touched.

   WHY v3 (both faults were found on a real board, not in theory)
   --------------------------------------------------------------
   1. v2 gave up waiting after 45s and took its baseline anyway. On
      custom-step-and-repeat-banner the board was still being rebuilt at that
      point - sb-dc-tplmerge.js was mid-way through merging 24 text layers into
      12 - so the baseline recorded layer ids that were deleted seconds later.
      Every set pointed at dead layers and nothing ever propagated, which is
      exactly the "I still have to edit each tile" report. v3 waits for settled
      and REBUILDS the baseline whenever the layers it is tracking disappear.
   2. v2 only acted when EXACTLY ONE layer in a set had changed. That held for
      a scripted edit but not for a real one: typing in a tile can make the app
      re-fit others in the same pass, and the set was then written off as a
      rebuild. v3 asks the app which layer is selected and treats that one as
      the edit, which is what the customer just touched.

   Rules, deliberately narrow:
     - Layers are grouped only if every tracked property matches exactly. Two
       tiles that already read differently are never linked.
     - Only CONTENT and STYLE propagate (PROPS below). Position, size and
       rotation never do - each tile keeps its own place on the board.
     - With nothing selected, the old rule still applies: one changed layer is
       an edit, several at once is a rebuild and is ignored.
     - Deleting a tile drops it from the set; the rest stay linked.

   FONT SIZE after an edit: each layer's size at baseline is kept as its
   ceiling, and after every propagation the set is set to the largest size that
   still fits its box, up to that ceiling. A long company name shrinks to fit,
   and shortening it again restores the original size.

   Off switch: window.__dcTileSyncOff = true (stops propagating immediately).
   Diagnostics: window.__dcTileSync() */
(function () {
  'use strict';

  var TICK = 400, START_MAX = 120000, SETTLE_TICKS = 2;
  var MARGIN = 0.98, FLOOR = 0.35, EPS = 0.01;
  var REBUILD_AT = 0.5;      // half the tracked layers gone = rebuild the baseline
  var COOL = 4;              // ticks a set is left alone after a propagation

  /* what follows an edit. Geometry is deliberately absent. */
  var PROPS = ['text', 'fill', 'stroke', 'strokeWidthIn', 'fontFamily', 'fontSizeIn',
               'bold', 'italic', 'underline', 'align', 'lineHeight', 'letterSpacing',
               'opacity', 'src', 'url', 'imageUrl'];

  /* what has to match for two layers to be considered copies */
  var KEY = ['type', 'text', 'fill', 'stroke', 'fontFamily', 'fontSizeIn', 'bold',
             'italic', 'align', 'lineHeight', 'src', 'url', 'imageUrl'];

  var info = { started: false, sets: 0, linked: 0, edits: 0, applied: 0,
               refits: 0, rebuilds: 0, skippedMulti: 0, cooled: 0, why: '' };
  window.__dcTileSync = function () { return info; };

  function q(name) {
    try { return new URLSearchParams(location.search).get(name) || ''; }
    catch (e) { return ''; }
  }
  if (!q('tpl')) { info.why = 'no tpl'; return; }

  function api() {
    var a = window.__dcApi;
    return (a && a.front && Array.isArray(a.front.layers) && typeof a.updateLayer === 'function') ? a : null;
  }

  function keyOf(l) {
    var s = '';
    for (var i = 0; i < KEY.length; i++) {
      var v = l[KEY[i]];
      s += '|' + (v == null ? '' : String(v));
    }
    return s;
  }

  function snapOf(l) {
    var o = {};
    for (var i = 0; i < PROPS.length; i++) {
      if (PROPS[i] in l) o[PROPS[i]] = l[PROPS[i]];
    }
    return o;
  }

  function differs(a, b) {
    for (var k in b) if (String(a[k]) !== String(b[k])) return true;
    for (var j in a) if (!(j in b)) return true;
    return false;
  }

  /* two-way: down to fit the box, back up to the baseline size when the text
     gets shorter again. */
  var ctx = null, baseFs = {};
  function refit(a, l) {
    if (!l || l.type !== 'text' || +l.curveDeg) return;
    if (!ctx) ctx = document.createElement('canvas').getContext('2d');
    var fam = l.fontFamily || 'Archivo';
    var font = (l.italic ? 'italic ' : '') + (l.bold ? '700 ' : '400 ') + '100px "' + fam + '"';
    try { if (document.fonts && document.fonts.check && !document.fonts.check(font)) return; } catch (e) {}
    ctx.font = font;
    var em = 0, lines = String(l.text == null ? '' : l.text).split(/\r?\n/);
    for (var i = 0; i < lines.length; i++) {
      var w = ctx.measureText(lines[i]).width / 100;
      if (w > em) em = w;
    }
    em *= (+l.stretchX || 1);
    if (!em) return;

    var box = +l.widthIn || 0, fs = +l.fontSizeIn || 0;
    if (!box || !fs) return;
    var ceiling = baseFs[l.id] || fs;
    var want = Math.min(ceiling, box * MARGIN / em);
    var floor = ceiling * FLOOR;
    if (want < floor) want = floor;
    if (Math.abs(want - fs) < fs * EPS) return;
    a.updateLayer(l.id, { fontSizeIn: want });
    info.refits++;
  }

  var sets = [], tracked = 0;

  function baseline(a) {
    var buckets = {}, layers = a.front.layers;
    layers.forEach(function (l) {
      if (!l || l.locked) return;
      if (l.type === 'text' && +l.fontSizeIn) baseFs[l.id] = +l.fontSizeIn;
      var k = keyOf(l);
      (buckets[k] = buckets[k] || []).push(l);
    });
    sets = [];
    var linked = 0;
    Object.keys(buckets).forEach(function (k) {
      var group = buckets[k];
      if (group.length < 2) return;
      sets.push({ ids: group.map(function (l) { return l.id; }), snap: snapOf(group[0]),
                  cool: 0, srcId: null });
      linked += group.length;
    });
    tracked = linked;
    info.sets = sets.length;
    info.linked = linked;
    info.started = true;
  }

  /* the layers we are tracking must still exist; when a rebuild replaces them
     (new ids) the old baseline is worthless and silently stops working. */
  function stale(live) {
    if (!tracked) return false;
    var alive = 0;
    sets.forEach(function (s) {
      s.ids.forEach(function (id) { if (live[id]) alive++; });
    });
    return alive < tracked * REBUILD_AT;
  }

  function tick(a) {
    var live = {}, layers = a.front.layers;
    layers.forEach(function (l) { live[l.id] = l; });

    if (stale(live)) { info.rebuilds++; baseline(a); return; }

    var selId = a.selectedId || null;

    sets.forEach(function (set) {
      var present = set.ids.filter(function (id) { return live[id]; });
      if (present.length !== set.ids.length) set.ids = present;
      if (set.ids.length < 2) return;

      /* a propagation is still landing. A big src takes several ticks to reach
         every tile, and a tile that has not caught up yet is NOT an edit - v3
         read one as the edit and pushed the old picture back over the board. */
      if (set.cool > 0) {
        set.cool--;
        info.cooled++;
        var src0 = set.srcId && live[set.srcId];
        if (src0) set.snap = snapOf(src0);
        return;
      }

      var changed = [];
      set.ids.forEach(function (id) {
        if (differs(snapOf(live[id]), set.snap)) changed.push(id);
      });
      if (!changed.length) return;

      var srcId = null;
      if (selId && changed.indexOf(selId) > -1) {
        srcId = selId;                       // the tile the customer is working on
      } else if (changed.length === 1) {
        srcId = changed[0];
      } else {
        info.skippedMulti++;                 // a rebuild or an undo, not an edit
        set.snap = snapOf(live[set.ids[0]]);
        return;
      }

      var src = live[srcId];
      var next = snapOf(src);
      info.edits++;

      set.ids.forEach(function (id) {
        if (id === srcId) return;
        var patch = {}, any = false;
        for (var k in next) {
          if (String(live[id][k]) === String(next[k])) continue;
          patch[k] = next[k];
          any = true;
        }
        if (!any) return;
        a.updateLayer(id, patch);
        info.applied++;
      });

      set.snap = next;
      set.srcId = srcId;
      set.cool = COOL;

      setTimeout(function () {
        var a2 = api();
        if (!a2) return;
        var map = {};
        a2.front.layers.forEach(function (l) { map[l.id] = l; });
        set.ids.forEach(function (id) { refit(a2, map[id]); });
        if (map[srcId]) set.snap = snapOf(map[srcId]);   // the font change is ours
      }, 60);
    });
  }

  var t0 = Date.now(), quiet = 0, lastCount = -1, stableCount = 0;
  var timer = setInterval(function () {
    if (window.__dcTileSyncOff) return;
    var a = api();
    if (!a) return;

    if (!info.started) {
      var m = window.__dcTplMerge && window.__dcTplMerge();
      var s = window.__dcTplShrink && window.__dcTplShrink();
      var ready = (!m || m.settled) && (!s || s.settled);

      var n = a.front.layers.length;
      if (n === lastCount) stableCount++; else stableCount = 0;
      lastCount = n;

      if (ready && stableCount >= SETTLE_TICKS) quiet++; else quiet = 0;
      if (quiet < SETTLE_TICKS && Date.now() - t0 < START_MAX) return;
      baseline(a);
      return;
    }

    try { tick(a); }
    catch (e) { info.why = 'error: ' + (e && e.message); }
  }, TICK);

  addEventListener('pagehide', function () { clearInterval(timer); });
})();
